/** File-overview KPIs for the Analyze & Report step, computed from the job's SOV rows. */
export interface SovReportOverview {
    primaryMeasureLabel: string;
    primaryMeasureValue: number | null;
    primaryMeasureField: string | null;
    locations: number;
    countries: number;
    states: number;
    concentrationLabel: string | null;
    concentrationName: string | null;
    concentrationValue: number | null;
    concentrationShare: number | null;
    freeOverlayAvailable: boolean;
    paidOverlayAvailable: boolean;
}

/** One row of the "Top locations" report section. */
export interface SovTopLocationRow {
    rank: number;
    street: string;
    city: string;
    state: string;
    country: string;
    occupancy: string;
    value: number | null;
}

/** The "Top locations" report section, ranked by a measure, from the job's SOV rows. */
export interface SovReportTopLocations {
    measureLabel: string;
    money: boolean;
    total: number;
    locations: number;
    rows: SovTopLocationRow[];
}

/** A file column offered in the report config (numeric = measure, else dimension; populated = % non-empty). */
export interface SovReportField {
    name: string;
    numeric: boolean;
    populated?: number;
}

/** One grouped item of a report chart section. */
export interface SovAggregateItem {
    label: string;
    value: number;
    count: number;
}

/** A report chart section aggregation (group-by dimension, sum/avg/count measure). */
export interface SovReportAggregate {
    dimension: string;
    measureLabel: string;
    aggregation: string;
    money: boolean;
    total: number;
    locations: number;
    items: SovAggregateItem[];
}

/** A paid overlay assigned to the account, offered on the enrich step. */
export interface SovPaidOverlayOption {
    id: string;
    name: string;
    provider: string;
}

/** Free + paid overlay completion for the Enhancing screen (from per-row stage status). */
export interface SovOverlayProgress {
    jobId: number;
    total: number;
    freeRemaining: number;
    paidRemaining: number;
    freeDone: boolean;
    paidDone: boolean;
}

/** One configured report section sent to the overview export (mirrors the on-screen chart config). */
export interface SovReportExportSection {
    title: string;
    dimension: string;
    measure: string;
    aggregation: string;
    presentation: string;
}

/* ---- Overview report (v2): one definition drives the screen and the Excel export ---------------- */

/** One group-by inside a section (geography level, or a provider/peril field). */
export interface SovReportSubsectionDef {
    key: string;
    title: string;
    dimension: string;
    included: boolean;
}

/** A section of the Overview report; `kind` = TOP_TABLE | DIMENSION | PROVIDER_GRID | MATRIX. */
export interface SovReportSectionDef {
    id: string;
    kind: string;
    title: string;
    scope: string;
    measure: string | null;
    aggregation: string;
    includeTable: boolean;
    subsections: SovReportSubsectionDef[];
    parentSectionId: string | null;
}

/** The full Overview report configuration - auto-built by the backend, tailored on screen. */
export interface SovReportDefinition {
    title: string;
    primaryMeasure: string | null;
    sections: SovReportSectionDef[];
}

/** One column of a computed table; `type` = TEXT | MONEY | PERCENT | COUNT | NUMBER. */
export interface SovComputedColumn {
    label: string;
    type: string;
}

/** One rendered table (a sub-table within a section). Cell values are raw; format by column type. */
export interface SovComputedTable {
    title: string | null;
    columns: SovComputedColumn[];
    rows: unknown[][];
    totalRow: unknown[] | null;
    coverage?: number | null;
    /** Measure summed over the whole portfolio (Top 10 locations), the denominator for a location's share. */
    portfolioTotal?: number | null;
    /** The definition subsection this table renders (Geographical Footprint rows). */
    subsectionKey?: string | null;
}

/** One computed section ready to render; one section = one worksheet in Excel. */
export interface SovComputedSection {
    id: string;
    kind: string;
    title: string;
    scope: string;
    tables: SovComputedTable[];
    parentSectionId: string | null;
}

/** One glossary category table: a type/category title and rows of [score/category, definition]. */
export interface SovGlossaryCategory {
    title: string;
    rows: string[][];
}

/** One provider's glossary block: a provider banner and its category tables. */
export interface SovGlossaryGroup {
    provider: string;
    categories: SovGlossaryCategory[];
}

/** The fully computed Overview report: KPI header plus every section's tables. */
export interface SovComputedReport {
    title: string;
    overview: SovReportOverview;
    sections: SovComputedSection[];
    glossary: SovGlossaryGroup[];
}

/** A dataset_ai field offered in the grouping filter's Field dropdown. */
export interface SovAiField {
    id: string;
    name: string;
    displayName: string;
    type: string;
    highCardinality: boolean;
}

export enum SovUploadStatus {
    /** Parsing is still running on the backend. */
    PARSING = "PARSING",
    /** File parsed successfully — the flow may continue to geocoding. */
    PARSED = "PARSED",
    /**
     * The job needs no wizard at all.
     *
     * Sent when the upload only updated records that already exist: those are
     * already geocoded and enriched, so the backend finishes the job outright
     * rather than walking the user through steps with nothing to do. The UI
     * returns to the map instead of opening the wizard.
     */
    COMPLETE = "COMPLETE",
    /** The file could not be parsed. */
    FAILED = "FAILED",
}

export interface SovUploadSheet {
    sheetName: string;
    estimatedRowCount: number;
    included: boolean;
    skipReason: string | null;
    currency: string | null;
    columnCount: number;
}

export interface SovUploadSummary {
    id: string;
    jobId: number;
    totalRecords: number;
    vaptIssuesFound: number;
    geocodingAttempted: number;
    geocodingSkippedHadCoordinates: number;
    geocodedRooftop: number;
    geocodedApproximation: number;
    geocodedNotFound: number;
    geocodingErrors: number;
    sheetsProcessed: number;
    sheetsSkipped: number;
    mergedCellsExpanded: number;
    rowsNew: number;
    rowsUpdate: number;
    rowsAmbiguous: number;
    /**
     * Rows flagged as identical to at least one other row in the job (every
     * uploaded column except maptycs_location_id, trimmed and case-folded). Flagged
     * during parsing and re-counted after a duplicate-review import; rows are
     * never auto-removed, so this stays > 0 until the user edits the file.
     */
    duplicateRowCount: number;
    processingStartedAt: number | null;
    processingCompletedAt: number | null;
    vaptCleanRecords: number;
}

/**
 * A progress frame pushed over SSE while a job is parsing.
 *
 * Confirmed against the implemented endpoint, e.g.
 *   progress  {"jobId":33,"status":"PARSING","totalRows":0}
 *   progress  {"jobId":33,"status":"PARSING","message":"Processed sheet A","totalRows":1879}
 *   done      {"jobId":33,"status":"PARSED","totalRows":1879,"sheets":[...]}
 *
 * Note the terminal frame carries `totalRows` and `sheets` but no `summary`
 * object — the review screen falls back to these. Every field stays optional so
 * a partial frame still moves the UI along.
 */
export interface SovJobEvent {
    jobId?: number;
    status?: SovUploadStatus;
    /** Row count parsed so far, and the final total on the terminal frame. */
    totalRows?: number;
    /** 0–100. Not currently sent — the UI shows an indeterminate spinner without it. */
    percent?: number;
    /** Free-text stage label, e.g. "Processed sheet A". */
    message?: string;
    /** Not currently sent; kept because the upload response uses this shape. */
    summary?: SovUploadSummary | null;
    sheets?: SovUploadSheet[];
    error?: string;
    /**
     * Duplicate-flagged row count, so a client can offer the duplicate-review
     * round trip without a second request just to count the list.
     *
     * Only on terminal frames, and only on the snapshot the server sends when
     * a connection opens on an already-finished job — the live `done` frame
     * emitted as parsing ends does not carry it. So a screen that needs this
     * reads it from a fresh connection (see MaiaUploadService.getJobEventSnapshot),
     * not from the stream it was already watching.
     */
    duplicateRowCount?: number;
}

/** Response of POST /sov/dataset/{datasetId}/upload. */
export interface SovUploadResponse {
    jobId: number;
    status: SovUploadStatus;
    summary: SovUploadSummary | null;
    /** Varies per uploaded workbook — always render from this array. */
    sheets: SovUploadSheet[];
    /** Present on failures in some responses. */
    message?: string;
    error?: string;
}

/**
 * Response of `GET /sov/account/{accountId}/dataset/{datasetId}/last-job` —
 * the dataset's most recent SOV job, or nothing at all when it has never had
 * one.
 *
 * A dataset has at most one job, and `active` is what decides whether "Add
 * Portfolio" resumes it or starts fresh.
 */
export interface SovLastJob {
    id: number;
    /** e.g. "COMPLETE". Not used to choose the route — `active` is. */
    status: string;
    /**
     * False once the job finished or was closed; it then rejects every change
     * with 409 SOV_JOB_CLOSED, so there is nothing a resumed wizard could do
     * with it.
     *
     * Optional because a job created before this flag existed has no value for
     * it, and the server works those out by the older "open" rule. Treat a
     * missing value as still open, never as closed.
     */
    active?: boolean;
    /**
     * Why it stopped being active — IMPORT_COMPLETED, CLOSED_BY_USER or
     * PARSE_FAILED. Null while the job is still active.
     */
    closedReason?: string | null;
}
